import { useMemo, useEffect } from 'react';
import { useReefConfig } from './UseReefConfig';
import { getWhiteLabelTheme, requiresWhiteLabelLogin, WhiteLabelTheme, ProductType } from '../config/white-label-config';

/**
 * Hook to access white label theme configuration
 * 
 * This hook provides access to the current product's theme configuration
 * including colors, logos, and other branding elements.
 * 
 * @returns Object containing theme configuration and utility functions
 */
export function useWhiteLabel() {
  const { product, hasLoaded } = useReefConfig();

  const theme = useMemo((): WhiteLabelTheme => {
    if (!hasLoaded) {
      // Return default theme while loading
      return getWhiteLabelTheme('atollon');
    }
    return getWhiteLabelTheme(product);
  }, [product, hasLoaded]);

  // Update document title and meta description when theme changes
  useEffect(() => {
    if (hasLoaded && theme) {
      // Update document title
      document.title = theme.siteTitle;

      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', theme.siteDescription);
      }
    }
  }, [theme, hasLoaded]);

  const needsWhiteLabelLogin = useMemo((): boolean => {
    if (!hasLoaded) {
      return false;
    }
    return requiresWhiteLabelLogin(product);
  }, [product, hasLoaded]);

  const isAtollon = useMemo((): boolean => {
    return product?.toLowerCase() === 'atollon' || !product;
  }, [product]);

  const isHealtek = useMemo((): boolean => {
    return product?.toLowerCase() === 'healtek';
  }, [product]);

  const isAgriflow = useMemo((): boolean => {
    return product?.toLowerCase() === 'agriflow';
  }, [product]);

  return {
    /** Current theme configuration */
    theme,
    /** Current product name */
    product: product?.toLowerCase() as ProductType,
    /** Whether the app should use the white label login page */
    needsWhiteLabelLogin,
    /** Whether the current product is Atollon */
    isAtollon,
    /** Whether the current product is Healtek */
    isHealtek,
    /** Whether the current product is Agriflow */
    isAgriflow,
    /** Whether the configuration has loaded */
    hasLoaded
  };
}
